import { WFabricWorld } from '@/littleWedding/WFabricWorld';
import { CUTTING_GROUP_NAME, CUTTING_OUTLINE_NAME, GRID_HELPER } from '@/littleWedding/fabricGroupsName';
import { Events } from '@/services/Events';
import { setCuttingPathVisibility } from '@/commons/fabricjs/FabricCallbackHelper';
import { BACKGROUND_MASK_DEFAULT_OPACITY, CUTTING_PATH_DEFAULT_OPACITY, MASK_MARGIN_PX } from '@/littleWedding/projectConstantes';
import { setBackgroundMaskVisibility } from '@/commons/fabricjs/FabricBackgroundMaskHelper';
import { Texture, SRGBColorSpace, MeshBasicMaterial } from 'three';
import { Canvas, Group, Rect } from 'fabric';
import type { Ref } from 'vue';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { IFabricImageCustom } from '@/typescript/interfaces/IFabricImageCustom';
import type { ITextboxCustom } from '@/typescript/interfaces/ITextboxCustom';

function _removeMask(fabricCanvas: Canvas): void {
    const cutpathGroupHelpers = fabricCanvas?.getObjects().filter((obj) => {
        return [CUTTING_GROUP_NAME].includes((obj as IFabricImageCustom | ITextboxCustom)?.id);
    });
    if (cutpathGroupHelpers?.length) {
        cutpathGroupHelpers.forEach((cutpathGroupHelper) => {
            fabricCanvas.remove(cutpathGroupHelper);
            fabricCanvas.renderAll();
        });
    }
}
function _removeOutline(fabricCanvas: Canvas): void {
    // next part remove blue outline of cutting path
    const childrenObject = fabricCanvas?.getObjects('group').filter((obj) => {
        return [CUTTING_OUTLINE_NAME].includes((obj as IFabricImageCustom | ITextboxCustom)?.id);
    });
    if (childrenObject.length > 0) {
        fabricCanvas.remove(childrenObject[0]);
    }
}

// A part of the fabric canvas define as a working zone
async function getZoneCanvas({
    fabricCanvas,
    imageRenderHeight,
    imageRenderWidth,
    isPreview = false,
    multiplier = 1,
}: {
    fabricCanvas: Canvas;
    imageRenderHeight: number;
    imageRenderWidth: number;
    isPreview?: boolean;
    multiplier?: number;
}): Promise<HTMLCanvasElement> {
    fabricCanvas?.renderAll();

    // Clone the fabric canvas to avoid modifying the original one
    const clonedFabricCanvas = await fabricCanvas?.clone(['objects', 'width', 'height', 'viewportTransform', 'id']);

    // Re-apply text styles as they are not cloned by default in some cases
    const originalTexts = fabricCanvas.getObjects('textbox') as ITextboxCustom[];
    const clonedTexts = clonedFabricCanvas?.getObjects('textbox') as ITextboxCustom[];

    clonedTexts.forEach((clonedText) => {
        const originalTextbox = originalTexts.find((txt) => txt.id === clonedText.id);
        if (originalTextbox) {
            clonedText.set({ styles: { ...originalTextbox.styles } });
        }
    });

    const groupWithRect: Group | undefined = clonedFabricCanvas.getObjects().find((object): object is Group => {
        return !(object as Group & { id: string })?.id;
    });
    let { height, width } = fabricCanvas;
    if (groupWithRect) {
        const workingZoneRect = groupWithRect._objects.find((obj) => obj.type === 'rect') as Rect;
        if (!workingZoneRect) {
            throw new Error('Rect not found in the group.');
        }
        ({ height, width } = workingZoneRect);
    }
    width = Math.floor(width * multiplier);
    height = Math.floor(height * multiplier);

    clonedFabricCanvas?.set({
        width,
        height,
        backgroundColor: 'transparent',
    });
    if (!clonedFabricCanvas) {
        return Promise.reject(new Error('Failed to clone fabric canvas'));
    }

    // Remove selections
    const activeObject = clonedFabricCanvas?.getActiveObject();
    if (activeObject) {
        activeObject.set({
            hasControls: false,
            hasBorders: false,
        });
        clonedFabricCanvas.renderAll();
    }

    // remove helper grid
    // FIXME: I don't know why there are two gridHelperGroup objects. It's added only once
    const gridGroupHelpers = clonedFabricCanvas?.getObjects().filter((obj) => {
        return [GRID_HELPER].includes((obj as IFabricImageCustom | ITextboxCustom)?.id);
    });

    if (gridGroupHelpers?.length) {
        gridGroupHelpers.forEach((gridGroupHelper) => {
            clonedFabricCanvas.remove(gridGroupHelper);
            clonedFabricCanvas.renderAll();
        });
    }

    if (isPreview) {
        setCuttingPathVisibility(clonedFabricCanvas, CUTTING_PATH_DEFAULT_OPACITY);
        setBackgroundMaskVisibility(clonedFabricCanvas, BACKGROUND_MASK_DEFAULT_OPACITY);
        // _removeOutline(clonedFabricCanvas); // on 11/12, client asked to keep outline on preview image
    } else {
        _removeMask(clonedFabricCanvas);
        _removeOutline(clonedFabricCanvas);
    }

    if (groupWithRect) {
        clonedFabricCanvas.remove(groupWithRect);
    }

    clonedFabricCanvas.backgroundColor = 'transparent';

    // Remove background fill
    clonedFabricCanvas.getObjects().forEach((object) => {
        if (object.type === 'Rect') {
            object.fill = 'transparent';
        } else {
            object.set({
                objectCaching: false, // Disable object caching for the rectangle
                dirty: true, // Mark the object as dirty to ensure it gets redrawn
            });
        }
    });

    clonedFabricCanvas.renderAll();

    const imgPixelWidth = imageRenderWidth;
    const imgPixelHeight = imageRenderHeight;

    // We want to rerender the fabricCanvas on the correct size
    const ratioXFromFabricToRealWorld = clonedFabricCanvas.width / imgPixelWidth;
    const ratioYFromFabricToRealWorld = clonedFabricCanvas.height / imgPixelHeight;

    clonedFabricCanvas.setViewportTransform([ratioXFromFabricToRealWorld, 0, 0, ratioYFromFabricToRealWorld, 0, 0]);
    clonedFabricCanvas.renderAll();

    // I have no idea why 3 is the good value here, it is related to buildMaskPath,
    // main unverified idea would be it is due to stroke on both side of mask
    const marginToCrop = MASK_MARGIN_PX * 3;
    // Crop to remove mask margins, then stretch back to target dimensions
    const croppedCanvas = clonedFabricCanvas.toCanvasElement(1, {
        left: MASK_MARGIN_PX,
        top: MASK_MARGIN_PX,
        width: imgPixelWidth - marginToCrop,
        height: imgPixelHeight - marginToCrop,
    });

    // Finally draw the cropped canvas into a new canvas with the exact target dimensions
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = imgPixelWidth;
    outputCanvas.height = imgPixelHeight;
    const ctx = outputCanvas.getContext('2d')!;

    // Disable image smoothing to prevent blur when upscaling
    ctx.imageSmoothingEnabled = false;

    ctx.drawImage(croppedCanvas, 0, 0, croppedCanvas.width, croppedCanvas.height, 0, 0, outputCanvas.width, outputCanvas.height);

    return outputCanvas;
}

async function canvasToBlob({
    fabricWorld,
    isPreview = false,
    multiplier = 1,
}: {
    fabricWorld: WFabricWorld;
    isPreview: boolean;
    multiplier?: number;
}): Promise<Blob | null> {
    if (!fabricWorld || !fabricWorld.fabricCanvas) {
        return null;
    }

    const zoneCanvas = await getZoneCanvas({
        fabricCanvas: fabricWorld.fabricCanvas,
        imageRenderHeight: fabricWorld.imageRenderHeight,
        imageRenderWidth: fabricWorld.imageRenderWidth,
        multiplier,
        isPreview,
    });

    return new Promise((resolve) => {
        zoneCanvas.toBlob(
            (blob) => {
                resolve(blob);
            },
            'image/png',
            1,
        );
    });
}

async function materialUpdater(params: {
    customMaterial: Ref<MeshBasicMaterial>;
    fabricWorld: WFabricWorld;
    isPreview: boolean;
}): Promise<void> {
    const { customMaterial, fabricWorld, isPreview = true } = params;

    const blob = await canvasToBlob({ fabricWorld, isPreview });

    if (!blob) {
        return;
    }
    const url = URL.createObjectURL(blob);

    const img = new Image();
    img.src = url;

    if (!img) {
        return;
    }
    img.onload = () => {
        const texture = new Texture(img);

        customMaterial.value.map = texture;
        customMaterial.value.map.generateMipmaps = false;
        customMaterial.value.transparent = true;
        customMaterial.value.map.needsUpdate = true;
        customMaterial.value.needsUpdate = true;
        customMaterial.value.map.colorSpace = SRGBColorSpace;
        customMaterial.value.map.flipY = false;
        Events.emit('rendered-onAfter');
    };
}

async function createThumbnailFromBat(params: { fabricWorld: WFabricWorld }): Promise<Blob | undefined> {
    const thumbnailWidth = 112 * 2;
    const thumbnailHeight = 160 * 2;
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = thumbnailWidth;
    finalCanvas.height = thumbnailHeight;
    const ctx = finalCanvas.getContext('2d');
    const { fabricWorld } = params;

    if (!fabricWorld.fabricCanvas || !ctx) {
        return Promise.reject(new Error('Fabric canvas or working zone or context is missing'));
    }
    const zoneCanvas = await getZoneCanvas({
        fabricCanvas: fabricWorld.fabricCanvas,
        imageRenderHeight: fabricWorld.imageRenderHeight,
        imageRenderWidth: fabricWorld.imageRenderWidth,
        isPreview: true,
    });
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
    const reductImage = canvasReduction(zoneCanvas, thumbnailWidth * 0.3, thumbnailHeight);
    const globalRatioFromReductionImage = reductImage.width / fabricWorld.imageRenderWidth;
    const marginRatio = globalRatioFromReductionImage;
    const ratio = thumbnailHeight / (reductImage.height - globalRatioFromReductionImage);
    const imgLeft = thumbnailWidth / 2 - (reductImage.width / 2) * ratio;
    const imgTop = -marginRatio;
    ctx.drawImage(reductImage, imgLeft, imgTop, reductImage.width * ratio, reductImage.height * ratio);
    const blob = await new Promise<Blob | undefined>((resolve) => {
        finalCanvas.toBlob(
            (b) => {
                if (b) {
                    resolve(b);
                } else {
                    resolve(undefined);
                }
            },
            'image/png',
            1,
        );
    });

    return blob;
}

/**
 * Allow the slow scale of an image, avoiding a brutal pixel compression with a smoother outcome
 * @param canvas
 * @param widthGoal
 * @param heightGoal
 * @enhance the calculation are sufficiente for it's purpose in this project
 * but could be improved working on the gap between goal target and actual canvas size
 * @returns
 */

function canvasReduction(canvas: HTMLCanvasElement, widthGoal?: number, heightGoal?: number, scalar: number = 0.75): HTMLCanvasElement {
    if (
        (!widthGoal && !heightGoal) ||
        (widthGoal && canvas.width * scalar <= widthGoal) ||
        (heightGoal && canvas.height * scalar <= heightGoal)
    ) {
        return canvas;
    }
    const { height, width } = canvas;
    const finalImageCanvas = document.createElement('canvas');
    const ctx = finalImageCanvas.getContext('2d');
    finalImageCanvas.width = Math.round(width * scalar);
    finalImageCanvas.height = Math.round(height * scalar);
    ctx?.drawImage(canvas, 0, 0, width, height, 0, 0, finalImageCanvas.width, finalImageCanvas.height);
    return canvasReduction(finalImageCanvas, widthGoal, heightGoal, scalar);
}

function addWatermarkToCanvas(fabricWorldInstance: WFabricWorld, imgLogoAsField: IImageField): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
        getZoneCanvas({
            fabricCanvas: fabricWorldInstance.fabricCanvas!,
            imageRenderHeight: fabricWorldInstance.imageRenderHeight,
            imageRenderWidth: fabricWorldInstance.imageRenderWidth,
            isPreview: true,
        })
            .then((clonedFabricCanvas) => {
                const imgLogo = new Image();
                imgLogo.src = imgLogoAsField.url || '';
                imgLogo.onload = () => {
                    applyWatermarkToCanvas({
                        clonedFabricCanvas,
                        watermarkImage: imgLogo,
                        xOffset: (imgLogoAsField.transform.size?.width ?? 369) * imgLogoAsField.transform.scale.x + 100,
                        yOffset: (imgLogoAsField.transform.size?.height ?? 66) * imgLogoAsField.transform.scale.y + 100,
                        scale: imgLogoAsField.transform.scale,
                        opacity: 1,
                    });
                    resolve(clonedFabricCanvas);
                };
            })
            .catch((error) => {
                reject(error);
            });
    });
}

// Reference canvas size for consistent watermark count across different document sizes
const WATERMARK_REFERENCE_WIDTH = 2000;

function applyWatermarkToCanvas({
    clonedFabricCanvas,
    opacity = 0.65,
    scale = { x: 2, y: 2 },
    watermarkImage,
    xOffset = 369,
    yOffset = 66,
}: {
    clonedFabricCanvas: Canvas | HTMLCanvasElement;
    opacity?: number;
    scale?: { x: number; y: number };
    watermarkImage: HTMLCanvasElement | HTMLImageElement;
    xOffset?: number;
    yOffset?: number;
}): void {
    const ctx = clonedFabricCanvas.getContext('2d');
    if (!ctx) {
        console.error('Canvas context not found');
        return;
    }

    ctx.globalAlpha = opacity;

    // Scale watermark size and offsets proportionally to canvas size
    // This ensures a constant number of watermarks regardless of document dimensions
    const canvasScaleFactor = clonedFabricCanvas.width / WATERMARK_REFERENCE_WIDTH;
    const adjustedScale = { x: scale.x * canvasScaleFactor, y: scale.y * canvasScaleFactor };
    const adjustedXOffset = xOffset * canvasScaleFactor;
    const adjustedYOffset = yOffset * canvasScaleFactor;

    const scaledWidth = watermarkImage.width * adjustedScale.x;
    const scaledHeight = watermarkImage.height * adjustedScale.y;

    let toggle = 0;
    for (let j = -adjustedYOffset; j < clonedFabricCanvas.height * 2; j += adjustedYOffset) {
        const horizontalOffset = toggle % 2 === 0 ? adjustedXOffset : adjustedXOffset * 0.25;
        for (let i = -horizontalOffset; i < clonedFabricCanvas.width * 2; i += adjustedXOffset * 1.5) {
            ctx.drawImage(watermarkImage, i, j, scaledWidth, scaledHeight);
        }
        toggle++;
    }

    ctx.globalAlpha = 1; // Reset opacity
}

function generateImageWithWatermarks(fabricWorld: WFabricWorld): Promise<Blob | undefined> {
    const imgBatWidth = fabricWorld.imageRenderWidth;
    if (!fabricWorld?.fabricCanvas) {
        return Promise.resolve(undefined);
    }
    const imgLogoAsField: IImageField = {
        filename: 'watermark',
        hasQualityWarning: false,
        id: 'watermark',
        url: './images/watermarkPMEA.svg',
        editable: false,
        frameSize: {
            height: 25,
            width: 150,
        },
        transform: {
            angle: 0, // 0-360deg,
            position: { x: 0, y: 0 },
            scale: { x: 3, y: 3 },
            size: {
                height: 50,
                width: 200,
            },
        },
    };
    return addWatermarkToCanvas(fabricWorld, imgLogoAsField).then(async (clonedCanvas) => {
        try {
            const reductionFactor = imgBatWidth / 2;

            const reducedCanvas = canvasReduction(clonedCanvas, undefined, reductionFactor);

            if (reducedCanvas) {
                return new Promise<Blob | undefined>((resolve) => {
                    reducedCanvas.toBlob(
                        (blob) => {
                            resolve(blob ?? undefined);
                        },
                        'image/png',
                        1,
                    );
                });
            } else {
                console.error('Failed');
                return undefined;
            }
        } catch (error) {
            console.error('Error: ', error);
            return undefined;
        }
    });
}

export {
    addWatermarkToCanvas,
    applyWatermarkToCanvas,
    canvasReduction,
    canvasToBlob,
    createThumbnailFromBat,
    generateImageWithWatermarks,
    materialUpdater,
};
